import React from 'react';
import type { Editor } from '@tiptap/react';

import { parseFrameDimensions } from '~/components/document-editor/editor-image-placeholder';
import { useImageUpload } from '~/components/document-editor/editor-image-upload-handler';

export type EditorResizeControlsState = {
  visible: boolean;
  width: number;
  height: number;
  pos: number;
  x: number;
  y: number;
} | null;

/**
 * Image upload handler and `onCreate` listeners for placeholders, uploads, and
 * resize control positioning.
 */
export function useEditorImageSetup(params: {
  workspaceId?: string;
  templateId?: string;
  artifactId?: string;
  setResizeControlsState: React.Dispatch<React.SetStateAction<EditorResizeControlsState>>;
}) {
  const { workspaceId, templateId, artifactId, setResizeControlsState } = params;

  // useImageUpload owns mutations, so it must be called on every render: a
  // caller whose workspaceId arrives asynchronously would otherwise change
  // the hook count between renders. Uploading is gated on having somewhere to
  // upload to instead.
  const uploader = useImageUpload({
    workspaceId,
    templateId,
    artifactId: artifactId || templateId || workspaceId || 'editor'
  });
  const imageUpload = workspaceId || templateId ? uploader : null;

  const onCreate = React.useCallback(
    ({ editor }: { editor: Editor }) => {
      const cleanups: Array<() => void> = [];

      if (imageUpload) {
        const handleImageUpload = async (event: Event) => {
          const customEvent = event as CustomEvent<{
            file: File;
            pos: number;
            identifier: string;
          }>;
          const { file, pos, identifier } = customEvent.detail;

          try {
            const { url, fileId } = await imageUpload.uploadImage(file);

            const nodeAtPos = editor.state.doc.nodeAt(pos);
            const isPlaceholder = nodeAtPos?.type.name === 'imagePlaceholder';

            if (isPlaceholder) {
              const frameDims = parseFrameDimensions(
                (nodeAtPos.attrs as { frameStyle?: string }).frameStyle
              );
              editor
                .chain()
                .focus()
                .deleteRange({ from: pos, to: pos + 1 })
                .insertContentAt(pos, {
                  type: 'image',
                  attrs: {
                    src: url,
                    alt: identifier || file.name,
                    'data-image-id': fileId,
                    'data-image-identifier': identifier,
                    textAlign: 'right',
                    ...frameDims
                  }
                })
                .run();
            } else {
              editor
                .chain()
                .focus()
                .setNodeSelection(pos)
                .updateAttributes('image', {
                  src: url,
                  alt: identifier || file.name,
                  'data-image-id': fileId,
                  'data-image-identifier': identifier
                })
                .run();
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Image upload error:', error);
            alert('Failed to upload image. Please try again.');
          }
        };

        editor.view.dom.addEventListener('image-placeholder-upload', handleImageUpload);
        cleanups.push(() => {
          editor.view.dom.removeEventListener('image-placeholder-upload', handleImageUpload);
        });
      }

      const handleImageSelected = (event: Event) => {
        const customEvent = event as CustomEvent<{
          pos: number;
          width: number;
          height: number;
        }>;
        const { pos, width, height } = customEvent.detail;

        const imageNode = editor.view.domAtPos(pos).node as HTMLElement;
        const imageElement = imageNode.querySelector('img') || imageNode;
        const rect = imageElement.getBoundingClientRect();

        setResizeControlsState({
          visible: true,
          width,
          height,
          pos,
          x: rect.right + 10,
          y: rect.top
        });
      };

      editor.view.dom.addEventListener('image-selected', handleImageSelected);
      cleanups.push(() => {
        editor.view.dom.removeEventListener('image-selected', handleImageSelected);
      });

      return () => {
        for (const cleanup of cleanups) cleanup();
      };
    },
    [imageUpload, setResizeControlsState]
  );

  return { onCreate };
}
